/* ============================================================
   MOONRISE — <collection-grid> custom element
   Phase B for sections/main-collection.liquid

   Three behaviors:
     1. Grid toggle — 4-col / 3-col on desktop. Toggles `.grid-3`
        on `.products-grid` and persists to localStorage.
     2. Sort dropdown — `change` reloads the page with the new
        `?sort_by=` value, preserving all other URL params except
        `page` (which always resets to 1 on sort change).
     3. Infinite scroll — IntersectionObserver on the bottom
        sentinel. Fetches the next page via Section Rendering API
        (`?page=N+1&section_id=<id>`), parses the response, appends
        new product cards, replaces the sentinel. Hides the
        fallback numbered pagination on first hydration.
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('collection-grid')) return;

  var COLS_STORAGE_KEY = 'moonrise:collection-cols';
  var MOBILE_BP = 768;

  function isDesktop() {
    return window.innerWidth > MOBILE_BP;
  }

  class CollectionGrid extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.grid = this.querySelector('[data-products-grid]');
      this.sentinel = this.querySelector('[data-infinite-sentinel]');
      this.fallbackPagination = this.querySelector('[data-fallback-pagination]');
      this.statusEl = this.querySelector('[data-grid-status]');
      if (!this.grid) return;

      this._loadedPages = 1;
      this._fetching = false;

      this._initGridToggle();
      this._initSort();
      this._initInfiniteScroll();
    }

    /* ─────────────── Grid toggle (4 ↔ 3 cols) ─────────────── */
    _initGridToggle() {
      var toggle = document.querySelector('[data-collection-grid-toggle]');
      if (!toggle) return;
      this.gridToggle = toggle;
      this.gridToggleBtns = Array.from(toggle.querySelectorAll('.grid-toggle-btn'));
      if (!this.gridToggleBtns.length) return;

      // Reveal once JS is present (CSS could hide it before hydration).
      toggle.removeAttribute('hidden-until-js');

      // Restore previous choice on desktop only.
      if (isDesktop()) {
        var saved = null;
        try { saved = localStorage.getItem(COLS_STORAGE_KEY); } catch (e) { /* private mode */ }
        if (saved === '3') this._applyCols('3');
        else this._applyCols('4');
      } else {
        // Mobile is always 2-col; CSS hides the toggle. Don't apply state.
      }

      var self = this;
      this.gridToggleBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var cols = btn.dataset.cols;
          self._applyCols(cols);
          try { localStorage.setItem(COLS_STORAGE_KEY, cols); } catch (e) { /* ignore */ }
        });
      });

      // Resize: if user shrinks to mobile we don't need to touch state
      // because CSS forces 2-col regardless. On growing back to desktop,
      // the existing grid-3/4 state on the DOM still applies.
    }

    _applyCols(cols) {
      var wantsThree = cols === '3';
      this.grid.classList.toggle('grid-3', wantsThree);
      this.gridToggleBtns.forEach(function (btn) {
        var isActive = btn.dataset.cols === cols;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    /* ─────────────── Sort dropdown ─────────────── */
    _initSort() {
      var select = document.querySelector('[data-collection-sort]');
      if (!select) return;
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page'); // reset to first page on sort change
        window.location.href = url.toString();
      });
    }

    /* ─────────────── Infinite scroll ─────────────── */
    _initInfiniteScroll() {
      if (!this.sentinel) return;
      if (!('IntersectionObserver' in window) || !this.sectionId) {
        // No IO support → leave fallback pagination visible.
        return;
      }

      // We're going to take over pagination — hide the fallback nav.
      if (this.fallbackPagination) {
        this.fallbackPagination.setAttribute('hidden', '');
      }

      var self = this;
      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) self._fetchNext();
        });
      }, { rootMargin: '400px 0px' });
      this._io.observe(this.sentinel);
    }

    _fetchNext() {
      if (this._fetching) return;
      var sentinel = this.sentinel;
      if (!sentinel) return;
      var nextUrl = sentinel.dataset.nextUrl;
      if (!nextUrl) return;

      this._fetching = true;
      this.grid.setAttribute('aria-busy', 'true');

      // Add section_id to scope the response to our section's HTML.
      var url;
      try {
        url = new URL(nextUrl, window.location.origin);
      } catch (e) {
        url = new URL(window.location.href);
        url.searchParams.set('page', String(this._loadedPages + 1));
      }
      url.searchParams.set('section_id', this.sectionId);

      var self = this;
      fetch(url.toString(), { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          self._appendFromHtml(html);
        })
        .catch(function () {
          // Network or parse error — restore fallback pagination so the
          // user can still navigate manually. Stop trying to fetch more.
          if (self._io) self._io.disconnect();
          if (self.fallbackPagination) self.fallbackPagination.removeAttribute('hidden');
        })
        .then(function () {
          self._fetching = false;
          self.grid.setAttribute('aria-busy', 'false');
        });
    }

    _appendFromHtml(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');

      // Grab fresh cards from the parsed grid.
      var freshGrid = doc.querySelector('[data-products-grid]');
      if (!freshGrid) {
        this._stop();
        return;
      }
      var freshCards = Array.from(freshGrid.querySelectorAll('.product-card'));
      if (freshCards.length === 0) {
        this._stop();
        return;
      }

      // Insert cards BEFORE any trailing skeleton placeholders.
      var firstSkeleton = this.grid.querySelector('[data-skeleton]');
      freshCards.forEach(function (card) {
        if (firstSkeleton) this.grid.insertBefore(card, firstSkeleton);
        else this.grid.appendChild(card);
      }, this);

      this._loadedPages += 1;
      if (this.statusEl) {
        this.statusEl.textContent = freshCards.length + ' more products loaded';
      }

      // Replace the sentinel with the freshly-rendered one (if any).
      var freshSentinel = doc.querySelector('[data-infinite-sentinel]');
      if (freshSentinel && freshSentinel.dataset.nextUrl) {
        this.sentinel.dataset.nextUrl = freshSentinel.dataset.nextUrl;
      } else {
        // No more pages — disconnect observer and remove sentinel.
        this._stop();
      }
    }

    _stop() {
      if (this._io) this._io.disconnect();
      if (this.sentinel && this.sentinel.parentNode) {
        this.sentinel.parentNode.removeChild(this.sentinel);
        this.sentinel = null;
      }
      if (this.statusEl) this.statusEl.textContent = 'All products loaded';
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }
  }

  customElements.define('collection-grid', CollectionGrid);
})();
